/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Selectbox, { option } from "../../components/Shared/SelectBox";
import Input from "../../components/Shared/Input";
import Button from "../../components/Shared/Button";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { queryClient } from "../../main";
import DeleteModal from "../../components/Shared/DeleteModal";
import TrashIcon from "../../components/Icons/TrashIcon";
import Spinner from "../../components/Shared/Spinner";
import { CreateCoupon } from "../../api/Coupon/AddCoupon";
import { GetCoupons } from "../../api/Coupon/GetAllCoupons";
import { DeleteCoupon } from "../../api/Coupon/DeleteCoupon";
import { useUserDataStore } from "../../stores/user";

const validationSchema = Yup.object({
  name: Yup.string().required("Title is required"),
  discount: Yup.string().required("Discount percentage is required"),
});
type coupon = option & {
  students: {
    id: string;
    email: string;
    fullName: string;
    phone: string;
  }[];
};
const Coupons = () => {
  const [selectedCoupon, setSelectedCoupon] = useState<coupon | null>(null);
  const { userData } = useUserDataStore();
  const userRole = userData?.role;

  const { mutate } = useMutation({
    mutationFn: CreateCoupon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coupon"] });
      toast.success("Coupon Added");
      formik.setSubmitting(true);
      formik.resetForm();
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });

  const { data: CouponResponse, isLoading: isCouponLoading } = useQuery({
    queryFn: GetCoupons,
    queryKey: ["coupon"],
    staleTime: 0,
    refetchInterval: 0,
  });

  const Coupons = CouponResponse?.data?.CouponData || [];
  const CouponsOptions = Coupons.map((coupon) => ({
    id: coupon.id,
    name: coupon.code,
    code: coupon.code,
    discount: coupon.discount,
    count: coupon.count,
    students: coupon.students || [], // Add students array
  }));

  const formik = useFormik({
    initialValues: {
      name: "",
      code: "",
      discount: "",
    },
    validationSchema,
    onSubmit: (values) => {
      mutate({
        ...values,
        discount: Number(values.discount),
      });
    },
  });

  const handleDelete = async (id: string) => {
    await DeleteCoupon(id);
    setSelectedCoupon(null);
    toast.success("Coupon deleted successfully");
  };
  const [isOpen, setIsOpen] = useState(false);

  const handleCouponSelection = (coupon: coupon) => {
    setSelectedCoupon({
      ...coupon,
      students: coupon.students || [],
    });
  };

  if (isCouponLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="py-7">
      <DeleteModal
        queryKey="coupon"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        mutationFn={handleDelete}
        itemId={selectedCoupon?.id || ""}
      />

      <h1 className="text-burntBlack font-bold text-[32px] mb-7">Coupons</h1>
      <div className="mb-8 p-8 rounded-xl bg-white border-black/15 shadow">
        <h1 className="font-bold text-2xl mb-8">Existing Coupons</h1>
        <Selectbox
          title="Select Coupon"
          selectOptions={CouponsOptions}
          placeholder="Choose a Coupon"
          selected={selectedCoupon}
          //@ts-ignore
          setSelected={handleCouponSelection}
        />
        {selectedCoupon && (
          <>
            <div>
              <h2 className="text-xl font-semibold mt-5">
                {selectedCoupon.name}
              </h2>
              <p className="font-semibold text-lg">
                Discount Code: {selectedCoupon.code}
              </p>
              <p className="font-semibold text-lg">
                Discount Percentage: {selectedCoupon.discount}
              </p>
              <p className="font-semibold text-lg">
                Count Used: {selectedCoupon.count}
              </p>
              <h3 className="mt-4 font-bold text-lg">Students:</h3>
              <ul className="list-disc">
                {selectedCoupon.students.map((student) => (
                  <li
                    key={student.id}
                    className="ml-4 grid grid-cols-3 font-medium "
                  >
                    <p>Name: {student.fullName}</p>
                    <p>Email: {student.email}</p>
                    <p>Phone: {student.phone || "N/A"}</p>
                  </li>
                ))}
              </ul>
            </div>
            <button
              onClick={() => setIsOpen(true)}
              className="mt-4 text-[#EF3826]"
            >
              <TrashIcon className="size-7" />
            </button>
          </>
        )}
      </div>
      {userRole === "admin" && (
        <div className="p-8 rounded-xl bg-white border-black/15 shadow">
          <h1 className="font-bold text-2xl mb-8">Add New Coupon</h1>
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            <div className="gap-4 flex">
              <Input
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.name && formik.errors.name
                    ? formik.errors.name
                    : ""
                }
                className="w-full"
                name="name"
                title="Title"
                inputStyles="!h-[50px]"
              />
              <Input
                value={formik.values.code}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full"
                name="code"
                title="Code"
                placeholder="ACCOUNTING15 (Optional)"
                inputStyles="!h-[50px]"
              />
            </div>
            <Input
              value={formik.values.discount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              type="number"
              error={
                formik.touched.discount && formik.errors.discount
                  ? formik.errors.discount
                  : ""
              }
              className="w-full"
              name="discount"
              title="Discount"
              inputStyles="!h-[50px]"
            />
            <Button
              disabled={formik.isSubmitting}
              className="w-64 !h-[50px] ml-auto"
              variant="primary"
            >
              Add Coupon
            </Button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Coupons;
